import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import IdentificationFields, { emptyIdent, identError } from "@/components/IdentificationFields";

/**
 * Register a new customer. Optionally pre-fills the phone number when the
 * cashier came here from the POS "customer not found" flow. The 18+ checkbox
 * is a staff-facing attestation only — it is NOT a legal age-verification
 * statement (the receipt/UI never claims otherwise).
 */
export default function RegisterCustomerDialog({
  open, onOpenChange, prefillPhone = "", onCreated, autoFocus = true,
}) {
  const [firstName, setFirstName] = useState("");
  const [surname, setSurname] = useState("");
  const [phone, setPhone] = useState("");
  const [ageOk, setAgeOk] = useState(true);
  const [ident, setIdent] = useState(emptyIdent);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setFirstName(""); setSurname(""); setPhone(prefillPhone || "");
      setAgeOk(true); setIdent(emptyIdent); setBusy(false);
    }
  }, [open, prefillPhone]);

  const submit = async () => {
    if (!firstName.trim() || !surname.trim()) return toast.error("First name and surname are required");
    if (!phone.trim()) return toast.error("Cellphone number is required");
    const idErr = identError(ident);
    if (idErr) return toast.error(idErr);
    setBusy(true);
    try {
      const r = await api.post("/customers", {
        first_name: firstName.trim(),
        surname: surname.trim(),
        phone: phone.trim(),
        age_confirmed_18_plus: ageOk,
        ...ident,
      });
      toast.success(`Customer created — ${r.data.first_name} ${r.data.surname}`);
      onCreated?.(r.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.customer_id) {
        toast.warning(`Cellphone already registered to ${detail.customer_name}`);
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Failed to create customer"));
      }
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="register-customer-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Register customer</DialogTitle>
          <DialogDescription>
            Capture the essentials. The 18+ box is a staff attestation; it does not by itself constitute legal age verification.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">First name</label>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="register-first-name" autoFocus={autoFocus} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Surname</label>
              <Input value={surname} onChange={(e) => setSurname(e.target.value)} data-testid="register-surname" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Cellphone number</label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="register-phone" placeholder="e.g. 0721234567" />
          </div>
          <IdentificationFields value={ident} onChange={setIdent} />
          <label className="flex items-start gap-2 text-xs" data-testid="register-age-label">
            <Checkbox checked={ageOk} onCheckedChange={(v) => setAgeOk(!!v)} data-testid="register-age-check" />
            <span>Customer confirms they are 18 years or older.</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="rounded-full bg-primary hover:bg-primary/90" data-testid="register-submit-btn">
            {busy ? "Saving…" : "Create customer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
